import * as React from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { getToken } from '../../../core/api-client';
import { monitoringApi } from '../api';
import { usePoll } from '../usePoll';
import { AgentStatusPill } from './AgentStatusPill';
import type { LiveStatusResponse, MonitoringLiveSession } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  employeeName: string;
}

/**
 * Loads the authed screenshot blob for a given screenshot id. Keeps the
 * PREVIOUS object-url alive until the next one has finished loading, so the
 * <img> never blinks to empty between frames. The caller drives this by id —
 * we only fetch when the id changes.
 */
function useLiveFrame(screenshotId: string | null): {
  url: string | null;
  loadedId: string | null;
} {
  const [url, setUrl] = React.useState<string | null>(null);
  const [loadedId, setLoadedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!screenshotId || screenshotId === loadedId) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const res = await fetch(monitoringApi.fileUrl(screenshotId), {
          headers: { Authorization: `Bearer ${getToken() ?? ''}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        // Swap only once the new frame is ready, then release the old url.
        setUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return objectUrl;
        });
        setLoadedId(screenshotId);
      } catch {
        // Keep the previous frame on a transient fetch error; next poll retries.
      }
    })();

    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshotId, loadedId]);

  // Release the final url on unmount.
  React.useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { url, loadedId };
}

function secondsUntil(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 1000));
}

function secondsSince(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(ms / 1000));
}

function fmtCountdown(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LiveViewPanel({ open, onOpenChange, employeeId, employeeName }: Props) {
  const confirm = useConfirm();
  const [session, setSession] = React.useState<MonitoringLiveSession | null>(null);
  const [status, setStatus] = React.useState<LiveStatusResponse | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [startError, setStartError] = React.useState<string | null>(null);
  // ticks every second to recompute countdown / "captured Ns ago"
  const [, tick] = React.useReducer((x) => x + 1, 0);

  const latestId = status?.latest_screenshot?.id ?? null;
  const { url, loadedId } = useLiveFrame(latestId);

  // (Re)start the live session — used on open, "Extend +5 min", and "Restart".
  const start = React.useCallback(async () => {
    setStarting(true);
    setStartError(null);
    try {
      const s = await monitoringApi.startLive(employeeId, { duration_seconds: 300, interval_seconds: 10 });
      setSession(s);
    } catch (err: any) {
      setStartError(err?.message ?? String(err));
      toast.error('Could not start live view', { description: err?.message });
    } finally {
      setStarting(false);
    }
  }, [employeeId]);

  // On open: kick off the session. On close: forget local state (do NOT stop).
  React.useEffect(() => {
    if (!open) {
      setSession(null);
      setStatus(null);
      setStartError(null);
      return;
    }
    void start();
  }, [open, start]);

  // Poll live status every 3s while open.
  usePoll(
    async () => {
      try {
        const s = await monitoringApi.liveStatus(employeeId);
        setStatus(s);
        if (s.session) setSession(s.session);
      } catch {
        // transient — keep last status
      }
    },
    3000,
    open,
  );

  // 1s clock for countdown + "captured Ns ago".
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open]);

  const expiresIn = secondsUntil(session?.expires_at);
  const expired = session != null && (expiresIn <= 0 || session.ended_at != null);
  const agentStatus = status?.agent.status ?? 'never';
  const isOnline = agentStatus === 'online';
  const isLegacyAgent = status != null && status.agent.last_seen_version == null;
  const hasFrame = url != null && loadedId != null;
  const capturedAgo = status?.latest_screenshot
    ? secondsSince(status.latest_screenshot.captured_at)
    : null;

  async function stop() {
    if (!session) return;
    if (!(await confirm({ title: 'Stop the live session?', description: 'This ends it for ALL viewers, not just you.' }))) return;
    try {
      await monitoringApi.stopLive(session.id);
      toast.success('Live session ended');
      onOpenChange(false);
    } catch (err: any) {
      toast.error('Could not stop session', { description: err?.message });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <div className="flex items-center gap-3 pr-8">
          <DialogTitle className="text-base">Live view — {employeeName}</DialogTitle>
          <AgentStatusPill status={agentStatus} lastHeartbeatAt={status?.agent.last_heartbeat_at} />
          <div className="ml-auto text-xs tabular-nums text-muted-foreground">
            {session && !expired && <>Ends in {fmtCountdown(expiresIn)}</>}
          </div>
        </div>

        {/* Degradation banners */}
        {startError && (
          <Banner tone="error">Could not start live view: {startError}</Banner>
        )}
        {!startError && status && !isOnline && !expired && (
          <Banner tone="warn">Agent appears offline — live view will begin when it reconnects.</Banner>
        )}
        {!startError && isLegacyAgent && !expired && (
          <Banner tone="info">
            This agent is v1.0.0 — it refreshes at most every capture interval. Update the agent for true live view.
          </Banner>
        )}

        {/* Viewport */}
        <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-muted">
          {hasFrame && (
            <img
              src={url!}
              alt={`Live screen of ${employeeName}`}
              className="h-full w-full object-contain"
            />
          )}

          {/* Waiting for first frame */}
          {!hasFrame && !expired && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
              <LoadingState variant="inline" />
              <span className="text-xs text-muted-foreground">
                {starting ? 'Starting live session…' : 'Waiting for agent (up to ~20s)…'}
              </span>
            </div>
          )}

          {/* "captured Ns ago" badge */}
          {hasFrame && capturedAgo != null && (
            <span className="absolute left-3 top-3 rounded-full bg-ink-900/75 px-2.5 py-1 text-[11px] font-semibold text-white tabular-nums backdrop-blur-sm">
              captured {capturedAgo}s ago
            </span>
          )}

          {/* Expiry overlay */}
          {expired && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink-900/70 text-center backdrop-blur-sm">
              <span className="text-sm font-semibold text-white">Live session ended</span>
              <Button size="sm" onClick={() => void start()} disabled={starting}>
                {starting ? 'Restarting…' : 'Restart'}
              </Button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={() => void start()}
            disabled={starting || !session}
          >
            Extend +5 min
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => void stop()}
            disabled={!session}
            className="ml-auto"
          >
            Stop session
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Closing this panel keeps the session running for other viewers. Use “Stop session” to end it for everyone.
        </p>
      </DialogContent>
    </Dialog>
  );
}

function Banner({ tone, children }: { tone: 'warn' | 'info' | 'error'; children: React.ReactNode }) {
  const cls =
    tone === 'error'
      ? 'bg-rose-500/10 text-rose-700 ring-rose-500/30 dark:bg-rose-500/15 dark:text-rose-200 dark:ring-rose-500/40'
      : tone === 'warn'
        ? 'bg-coral-100 text-coral-700 ring-coral-200 dark:bg-coral-500/15 dark:text-coral-200 dark:ring-coral-500/40'
        : 'bg-lavender-100 text-lavender-700 ring-lavender-200 dark:bg-lavender-400/15 dark:text-lavender-200 dark:ring-lavender-400/40';
  return (
    <div className={['rounded-xl px-3.5 py-2.5 text-[12.5px] ring-1', cls].join(' ')}>
      {children}
    </div>
  );
}
